import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { origin } from "../lib/origin";
import {
  MIN_REVIEWS_TO_SCORE,
  REPUTATION_DELTAS_PUBLISHED,
  TRUST_SCORE_VERSION,
  TRUST_SCORE_WEIGHTS,
  TRUST_SCORE_CHANGELOG,
} from "../../shared/methodology";
import { METHODOLOGY_FAQ } from "../../shared/faq";

export function Methodology() {
  useMeta(metaFor({ route: "methodology", base: origin() }));
  return (
    <div className="narrow-lg">
      <h1>Methodology</h1>
      <p className="muted">Version {TRUST_SCORE_VERSION} · the editorial wall between money and ratings is the product.</p>

      <h2 className="section">Proof-of-usage verification</h2>
      <p>
        Crypto uniquely lets us prove a reviewer actually used the thing they're reviewing. We offer
        tiered, cryptographic verification:
      </p>
      <ul>
        <li><strong>Verified Holder</strong> — you connect a wallet and sign a message; we confirm on-chain that the address holds (or held) the token. The signature is free and authorizes no transaction.</li>
        <li><strong>Verified User</strong> — for exchanges and companies, prove account activity with a read-only API key. We verify, then discard the secret immediately and store only a hashed reference.</li>
        <li><strong>Verified On-chain</strong> — for protocols and dapps, we confirm your address interacted with the contracts.</li>
      </ul>
      <p>Unverified reviews are allowed, clearly marked, rate-limited harder, and weighted far lower.</p>

      <h2 className="section">Reputation-weighted ratings</h2>
      <p>
        The headline score is not a naive average — naive averages are trivial to game. Each review's
        weight rises with the reviewer's verification level, reputation, and account history, and new
        accounts are dampened during a cool-down. We always show both the raw mean and the weighted
        score so nothing is hidden.
      </p>
      <p>
        Reputation itself moves only through the events below. Every change is written to an
        append-only log, so any reviewer&rsquo;s score can be reconstructed from its history.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Event</th><th>Change</th></tr>
          </thead>
          <tbody>
            {REPUTATION_DELTAS_PUBLISHED.map((r) => (
              <tr key={r.kind}>
                <td>{r.label}</td>
                <td className="num">{r.delta > 0 ? `+${r.delta}` : r.delta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="section">The editorial wall</h2>
      <p>
        Paid status, claimed status, and featured placement <strong>never</strong> influence the
        organic rating or sort order. Our ranking function takes zero input from any billing or claim
        data — this is enforced in code and tested. Featured items, if any, appear only in clearly
        labeled slots.
      </p>

      <h2 className="section">Trust Score v{TRUST_SCORE_VERSION}</h2>
      <p>
        The CryptoChit Trust Score combines objective signals with verified-review sentiment into a
        single transparent number from 0 to 100. These are the exact weights the scoring code uses
        this table is generated from the same constants, so it cannot drift from the formula.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Component</th><th>Weight</th><th>What it measures</th></tr>
          </thead>
          <tbody>
            {TRUST_SCORE_WEIGHTS.map((w) => (
              <tr key={w.key}>
                <td>{w.label}</td>
                <td className="num">{Math.round(w.weight * 100)}%</td>
                <td className="muted">{w.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        We show a Trust Score only when an entity has at least {MIN_REVIEWS_TO_SCORE} published
        reviews and enough signals to be meaningful. Below that we say we cannot score it rather
        than publishing a confident-looking number built on two reviews. Components that cannot
        exist for an entity type market depth for a company, for instance have their weight
        redistributed across the rest, so nothing is penalised for a signal it could never have.
      </p>
      <p className="muted">
        Contract security flags come from GoPlus, a third-party scanner. When a scan has not
        succeeded, the security component is <strong>absent</strong> rather than assumed clean. a
        missing scan lowers our confidence in the score, it never counts as a clean bill of health.
        Audit status stays operator-curated: there is no reliable programmatic audit registry, and
        guessing would be worse than saying we don&rsquo;t know.
      </p>
      <p className="muted">
        We will <strong>never</strong> ask you for an exchange API key. Verified User works through
        read-only OAuth: you authorise a read-only scope at your exchange, we confirm account
        activity once, and we discard the token immediately it is never stored. If a site claiming
        to be CryptoChit asks you to paste an API key, it is not us.
      </p>

      <h2 className="section">Changes to this formula</h2>
      <p>
        Re-weighting moves every score on the site, so each change is published here rather than
        shipped quietly.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Version</th><th>Date</th><th>What changed</th></tr>
          </thead>
          <tbody>
            {TRUST_SCORE_CHANGELOG.map((c) => (
              <tr key={c.version}>
                <td className="num">v{c.version}</td>
                <td className="num">{c.date}</td>
                <td className="muted">{c.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The same array the head serialises as FAQPage. Google treats visible
          text that differs from the structured data as a violation, and it
          would be dishonest besides — so there is one source for both. */}
      <h2 className="section" id="faq">Questions people ask</h2>
      <div className="faq">
        {METHODOLOGY_FAQ.map((f) => (
          <details key={f.q} className="faq-item">
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>

      <div className="nfa section">
        <strong>Not financial advice.</strong> CryptoChit publishes opinions and verifiable facts, not
        recommendations to buy, sell, or hold.
      </div>
    </div>
  );
}
